import {
  ChartPie,
  Tray,
  Gear,
  Buildings,
  UserCircle,
  Database,
  Monitor,
  PuzzlePiece,
  ChartLine,
  Speedometer,
  Table,
  Checks,
  Hospital,
  ShoppingCartSimple,
  Strategy,
  Briefcase,
  FileText,
  ChartBar,
  TShirt,
  BookOpen,
  Shapes,
  GlobeSimple,
  Flag,
  GitBranch,
  Storefront,
} from "@phosphor-icons/react"
import { useFlags } from "~/features/feature-flags"
import { flatten } from "lodash"

import { z } from "zod"
import { useUser } from "~/providers/userProvider"
import { useAllocationAccess } from "~/hooks/useAllocationAccess"
import { canAccessDataValidation, isProduction } from "~/utils/environment"
import { useMemo } from "react"
import useShowInternalFeatures from "~/hooks/useShowInternalFeatures"
import useShowHelix from "~/hooks/useShowHelix"
import { useCompany } from "~/hooks/useCompany"
import { getPrototypeSidebarItem } from "~/hooks/prototypeSidebarItems"

const SidebarSubItemSchema = z.object({
  name: z.string(),
  pathName: z.string(),
  disabled: z.boolean().optional(),
  isButton: z.boolean().optional(),
  icon: z.any(),
})

const SidebarItemSchema = z.object({
  name: z.string(),
  pathName: z.string(),
  icon: z.any(),
  subItems: z.array(SidebarSubItemSchema).optional(),
  disabled: z.boolean().optional(),
  disabledTooltip: z.string().optional(),
  exclude: z.boolean().optional(),
  isButton: z.boolean().optional(),
  section: z.string().optional(),
  matchPaths: z.array(z.string()).optional(),
})

const SidebarItemsSchema = z.array(SidebarItemSchema)

// Stable identity so memos keyed on the items survive re-renders for non-customer orgs.
const NO_ITEMS: z.infer<typeof SidebarItemsSchema> = []

const flattenSidebarItems = (items: z.infer<typeof SidebarItemsSchema>) => {
  const flattenedItems = items.map((item) => {
    if (item.subItems) {
      return [item, ...item.subItems]
    }
    return item
  })

  return flatten(flattenedItems)
}

const inboxIcon = (color: string) => <Tray className="cursor-pointer" color={color} />
const budgetIcon = (color: string) => (
  <div className="relative cursor-pointer">
    <ChartPie color={color} />
  </div>
)
const demandPlanningIcon = (color: string) => <ChartBar className="cursor-pointer" color={color} />
const salesOperationsIcon = (color: string) => <Strategy className="cursor-pointer" color={color} />
const procurementIcon = (color: string) => (
  <ShoppingCartSimple className="cursor-pointer" color={color} />
)
const settingsIcon = (color: string) => <Gear className="cursor-pointer" color={color} />
const organizationsIcon = (color: string) => <Buildings className="cursor-pointer" color={color} />
const customerSuccessIcon = (color: string) => <Hospital className="cursor-pointer" color={color} />
const usersIcon = (color: string) => <UserCircle className="cursor-pointer" color={color} />
const dataStatusIcon = (color: string) => <Database className="cursor-pointer" color={color} />
const tvIcon = (color: string) => <Monitor className="cursor-pointer" color={color} />
const componentsIcon = (color: string) => <PuzzlePiece className="cursor-pointer" color={color} />
const chartsIcon = (color: string) => <ChartLine className="cursor-pointer" color={color} />
const debugBarIcon = (color: string) => <Speedometer className="cursor-pointer" color={color} />
const tableIcon = (color: string) => <Table className="cursor-pointer" color={color} />
const productManagementIcon = (color: string) => (
  <Briefcase className="cursor-pointer" color={color} />
)
const collectionManagementIcon = (color: string) => (
  <TShirt className="cursor-pointer" color={color} />
)
const marketStrategyIcon = (color: string) => (
  <GlobeSimple className="cursor-pointer" color={color} />
)
const reportingIcon = (color: string) => <FileText className="cursor-pointer" color={color} />
const designSystemIcon = (color: string) => <Shapes className="cursor-pointer" color={color} />

const ChecksIcon = (color: string) => <Checks className="cursor-pointer" color={color} />
const cockpitIcon = (color: string) => <GitBranch className="cursor-pointer" color={color} />
const docsIcon = (color: string) => <BookOpen className="cursor-pointer" color={color} />
const featureFlagsIcon = (color: string) => <Flag className="cursor-pointer" color={color} />
const allocationIcon = (color: string) => <Storefront className="cursor-pointer" color={color} />

export const useSidebarItems = () => {
  const {
    budget: budgetFlag,
    reporting: reportingFlag,
    customerInsightsPage: customerInsightsPageFlag,
    orderSuggestionsPage: orderSuggestionsPageFlag,
    noosSupplyPlan: noosSupplyPlanFlag,
    ingestionCockpit: ingestionCockpitFlag,
  } = useFlags()

  const { permissions } = useUser()
  const allocationAccess = useAllocationAccess()
  const [showInternalFeatures] = useShowInternalFeatures()
  const showHelix = useShowHelix()
  const { data: company } = useCompany()

  const topSidebarItems = useMemo(
    () =>
      SidebarItemsSchema.parse([
        {
          name: "Inbox",
          pathName: "/inbox",
          icon: inboxIcon,
        },
        {
          name: "Budgets",
          pathName: "/budgets",
          disabled: !budgetFlag,
          icon: budgetIcon,
        },
        // The old module stays for everyone until Helix replaces it; once an org is in
        // the Helix beta only internal users keep seeing both side by side.
        ...(showHelix && !showInternalFeatures
          ? []
          : [
              {
                name: "Demand Planning",
                pathName: "/demand-planning",
                icon: demandPlanningIcon,
                subItems: [
                  {
                    name: "Fashion",
                    pathName: "/demand-planning/fashion",
                    icon: demandPlanningIcon,
                  },
                  {
                    name: "NOOS",
                    pathName: "/demand-planning/noos",
                    icon: demandPlanningIcon,
                  },
                ],
              },
            ]),
        // Hakio users outside production, or any org flagged into the beta
        // (see useShowHelix).
        ...(showHelix
          ? [
              {
                name: "Demand",
                pathName: "/demand",
                icon: demandPlanningIcon,
              },
            ]
          : []),
        {
          name: "Procurement",
          icon: procurementIcon,
          pathName: "/procurement",
          subItems: [
            {
              name: "Supply Plan",
              pathName: "/procurement/supply-plan",
              icon: procurementIcon,
            },
            ...(noosSupplyPlanFlag
              ? []
              : [
                  {
                    name: "Order Suggestions",
                    pathName: "/procurement/order-suggestions",
                    icon: procurementIcon,
                    disabled: !orderSuggestionsPageFlag,
                  },
                ]),
            {
              name: "Purchase Orders",
              pathName: "/procurement/purchase-orders",
              icon: procurementIcon,
            },
            {
              name: "Incoming Orders",
              pathName: "/procurement/incoming-orders",
              icon: procurementIcon,
            },
            {
              name: "Vendor Management",
              pathName: "/procurement/vendor-management",
              icon: procurementIcon,
            },
          ],
        },
        {
          name: "Insights",
          pathName: "/insights",
          icon: salesOperationsIcon,
          subItems: [
            {
              name: "Studio",
              pathName: "/insights/studio",
              icon: salesOperationsIcon,
            },
            // Under Helix the calendar lives in the Demand module instead.
            ...(showHelix
              ? []
              : [
                  {
                    name: "Demand Calendar",
                    pathName: "/insights/forecast-calendar",
                    icon: salesOperationsIcon,
                  },
                ]),
            {
              name: "Availability",
              pathName: "/insights/availability",
              icon: salesOperationsIcon,
            },

            {
              name: "Inventory",
              pathName: "/insights/inventory-overview",
              icon: salesOperationsIcon,
            },
            {
              name: "Customer Insights",
              pathName: "/insights/customer-insights",
              icon: salesOperationsIcon,
              disabled: !customerInsightsPageFlag,
            },
          ],
        },

        {
          name: "Reporting",
          icon: reportingIcon,
          pathName: "/reporting",
          disabled: !reportingFlag,
        },
        {
          name: "Allocation",
          pathName: allocationAccess.landingPath,
          icon: allocationIcon,
          matchPaths: ["/allocation"],
          exclude: !allocationAccess.showInSidebar,
        },
      ]).filter((item) => !item.exclude),
    [
      budgetFlag,
      reportingFlag,
      customerInsightsPageFlag,
      orderSuggestionsPageFlag,
      noosSupplyPlanFlag,
      showHelix,
      showInternalFeatures,
      permissions.isStaff,
      allocationAccess,
    ],
  )

  const bottomSidebarItems = useMemo(
    () =>
      SidebarItemsSchema.parse([
        {
          name: "Product Management",
          pathName: "/product-management",
          icon: productManagementIcon,
        },
        {
          name: "Collection Management",
          pathName: "/collection-management",
          icon: collectionManagementIcon,
          matchPaths: ["/product"],
        },
        // Ships with Helix: its first content, Planning scope, is a Demand 2.0 concept.
        ...(showHelix
          ? [
              {
                name: "Market Strategy",
                pathName: "/market-strategy",
                icon: marketStrategyIcon,
                exclude: company?.lifecycleStage === "not_customer",
                disabled: !permissions.canAdminister,
                disabledTooltip: permissions.canAdminister
                  ? undefined
                  : "Only admins can access Market Strategy. Contact an admin to change these settings.",
              },
            ]
          : []),
        {
          name: "Settings",
          pathName: "/settings",
          icon: settingsIcon,
          exclude: company?.lifecycleStage === "not_customer",
          disabled: !permissions.canAdminister,
          disabledTooltip: permissions.canAdminister
            ? undefined
            : "Only admins can access Settings. Contact an admin to change them.",
        },
        {
          name: "Data Validation",
          pathName: "/data-validation",
          icon: ChecksIcon,
          exclude: !canAccessDataValidation(company?.lifecycleStage),
        },
        {
          name: "Data Status",
          pathName: "/internal/status",
          icon: dataStatusIcon,
          exclude: !permissions.isStaff || !showInternalFeatures,
          section: "Internal",
        },
        {
          name: "Ingestion Cockpit",
          pathName: "/ingestion-cockpit",
          icon: cockpitIcon,
          exclude: !permissions.isStaff || !showInternalFeatures || ingestionCockpitFlag === false,
          section: "Internal",
        },
        {
          name: "Docs",
          pathName: "/internal/docs",
          icon: docsIcon,
          exclude: !permissions.isStaff || !showInternalFeatures,
          section: "Internal",
        },
        {
          name: "Customer Success",
          pathName: "/internal/customer-success",
          icon: customerSuccessIcon,
          exclude: !permissions.isStaff || !showInternalFeatures,
          section: "Internal",
          subItems: [
            {
              name: "Users",
              pathName: "/internal/customer-management/users",
              icon: usersIcon,
            },
            {
              name: "Organizations",
              pathName: "/internal/customer-management/organizations",
              icon: organizationsIcon,
            },
          ],
        },
        {
          name: "Feature flags",
          pathName: "/internal/feature-flags",
          icon: featureFlagsIcon,
          exclude: !permissions.isStaff || !showInternalFeatures,
          section: "Internal",
        },
        {
          name: "Design System",
          pathName: "/internal/design-system",
          icon: designSystemIcon,
          exclude: !permissions.isStaff || !showInternalFeatures,
          section: "Internal",
        },
        {
          name: "Others",
          pathName: "/internal/others",
          icon: componentsIcon,
          exclude: !permissions.isStaff || !showInternalFeatures,
          section: "Internal",
          subItems: [
            {
              name: "TV",
              pathName: "/internal/tv",
              icon: tvIcon,
            },
            {
              name: "Components (legacy)",
              pathName: "/internal/development/components",
              icon: componentsIcon,
              disabled: isProduction(),
            },
            {
              name: "Charts",
              pathName: "/internal/development/charts",
              icon: chartsIcon,
              disabled: isProduction(),
            },
            {
              name: "Toggle Debug Bar",
              pathName: "#",
              icon: debugBarIcon,
              isButton: true,
            },
            {
              name: "Forecast Reporting",
              pathName: "/internal/forecasts/forecasting-reports",
              icon: tableIcon,
            },
            {
              name: "Forecast Validation",
              pathName: "/internal/forecasts/forecast-validation-check",
              icon: ChecksIcon,
            },
          ],
        },
        getPrototypeSidebarItem({
          isStaff: permissions.isStaff,
          showInternalFeatures,
        }),
      ]).filter((item) => !item.exclude),
    [
      permissions.isStaff,
      permissions.canAdminister,
      showInternalFeatures,
      company?.lifecycleStage,
      showHelix,
      ingestionCockpitFlag,
    ],
  )

  const flattenedSidebarItems = useMemo(
    () =>
      flattenSidebarItems(
        [...topSidebarItems, ...bottomSidebarItems].filter((item) => !item.isButton),
      ),
    [topSidebarItems, bottomSidebarItems],
  )

  if (
    company !== undefined &&
    (!company.lifecycleStage || company.lifecycleStage === "not_customer")
  ) {
    return { topSidebarItems: NO_ITEMS, flattenedSidebarItems: NO_ITEMS, bottomSidebarItems }
  }

  return { topSidebarItems, flattenedSidebarItems, bottomSidebarItems }
}
